/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useState } from 'react';

import type { UserModel } from '@/domain/models/user.js';
import type { UserController } from '@/presentation/controllers/user-controller.js';
import type { UserProfile } from '@/shared/utils/format-status.js';

type State = {
    users: UserModel[];
    total: number;
    page: number;
    totalPages: number;
    isLoading: boolean;
    error: string | null;
};

export function useUsers(controller: UserController) {
    const [state, setState] = useState<State>({ users: [], total: 0, page: 1, totalPages: 1, isLoading: true, error: null });
    const [search, setSearch] = useState('');
    const [profileFilter, setProfileFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimitState] = useState(10);

    const setLimit = useCallback((value: number) => {
        setLimitState(value);
        setPage(1);
    }, []);

    const load = useCallback(async () => {
        setState((s) => ({ ...s, isLoading: true, error: null }));
        const result = await controller.loadUsers({ search: search || undefined, profile: profileFilter || undefined, status: statusFilter || undefined, page, limit });
        result.fold(
            (error) => setState({ users: [], total: 0, page: 1, totalPages: 1, isLoading: false, error: error.message }),
            (data) => setState({ users: data.data, total: data.total, page: data.page, totalPages: data.totalPages, isLoading: false, error: null })
        );
    }, [controller, search, profileFilter, statusFilter, page, limit]);

    useEffect(() => {
        load();
    }, [load]);

    const createUser = useCallback(
        async (params: { name: string; email: string; phone?: string; assignments: Array<{ clientId: string; projectId?: string; profile: UserProfile; isActive: boolean }> }) => {
            const result = await controller.createUser(params);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [controller, load]
    );

    const updateUser = useCallback(
        async (id: string, params: { name: string; phone?: string }) => {
            const result = await controller.updateUser(id, params);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [controller, load]
    );

    const blockUser = useCallback(
        async (id: string) => {
            const result = await controller.blockUser(id);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [controller, load]
    );

    const unblockUser = useCallback(
        async (id: string) => {
            const result = await controller.unblockUser(id);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [controller, load]
    );

    const resetUserPassword = useCallback(
        async (id: string) => {
            const result = await controller.resetUserPassword(id);
            return result.isRight();
        },
        [controller]
    );

    return {
        ...state,
        search,
        setSearch,
        profileFilter,
        setProfileFilter,
        statusFilter,
        setStatusFilter,
        page,
        setPage,
        limit,
        setLimit,
        reload: load,
        createUser,
        updateUser,
        blockUser,
        unblockUser,
        resetUserPassword
    };
}
